/**
 * @fileoverview Value picker for one filter condition (MOR-906 §7.8)
 *
 * Purpose: the searchable multi-select value list and its Apply / Apply NOT
 * footer, shared by the boolean expression builder and the pivot field
 * panel's Filters zone so a condition is composed the same way in both.
 *
 * The dimensions themselves are resolved once in `useEntryQueryDimensions`;
 * their shape lives here because both consumers of the picker need it.
 *
 * Used by:
 * - src/components/timesheets/EntryQueryBuilder.tsx
 * - src/components/timesheets/PivotFieldPanel.tsx
 */

import type { ReactNode } from "react";
import { Check } from "lucide-react";

import type { QueryField } from "@cvx/timesheets/query/entryQuery";

import { cn } from "@/utils/misc";
import { Button } from "@/ui/primitives";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/ui/composed/menus/command";

export interface EntryQueryOption {
  value: string;
  label: string;
}

/** A filterable dimension and the values it offers. */
export interface EntryQueryDimension {
  field: QueryField;
  label: string;
  options: ReadonlyArray<EntryQueryOption>;
  /** Optional richer option rendering (category glyphs, etc.). */
  renderOption?: (option: EntryQueryOption) => ReactNode;
}

/**
 * Pick the values one condition tests, then commit it.
 *
 * @param onApply - `negate` is the "is none of" form; NOT lives on the
 * condition because every entry dimension is single-valued.
 */
export function ConditionValueList({
  dimension,
  values,
  onValuesChange,
  onApply,
}: {
  dimension: EntryQueryDimension | undefined;
  values: string[];
  onValuesChange: (values: string[]) => void;
  onApply: (negate: boolean) => void;
}) {
  return (
    <>
      <Command>
        <CommandInput
          placeholder={`Search ${(dimension?.label ?? "values").toLowerCase()}…`}
        />
        <CommandList>
          <CommandEmpty>No matches.</CommandEmpty>
          <CommandGroup>
            {(dimension?.options ?? []).map((option) => {
              const selected = values.includes(option.value);
              return (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() =>
                    onValuesChange(
                      selected
                        ? values.filter((v) => v !== option.value)
                        : [...values, option.value],
                    )
                  }
                >
                  <div
                    className={cn(
                      "mr-2 flex size-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                      selected
                        ? "bg-primary text-primary-foreground"
                        : "opacity-50 [&_svg]:invisible",
                    )}
                  >
                    <Check className="size-3" />
                  </div>
                  <span className="min-w-0 flex-1">
                    {dimension?.renderOption
                      ? dimension.renderOption(option)
                      : option.label}
                  </span>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
      <div className="flex items-center justify-end gap-1.5 border-t border-border/60 p-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7 text-xs"
          disabled={values.length === 0}
          onClick={() => onApply(true)}
          title="Exclude entries matching these values"
        >
          Apply NOT
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 text-xs"
          disabled={values.length === 0}
          onClick={() => onApply(false)}
        >
          Apply
        </Button>
      </div>
    </>
  );
}
